import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { personalGet, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, printTable } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';
import { PERSONAL_RESUMES_BASE, type AggregateResume } from '@wport/core';
import { SECTIONS, type SectionDef } from '@wport/core';

interface ViewFlags {
	fields?: string;
}

/**
 * `resumes view` / `resumes export` 共用抓取邏輯：GET /resumes/{enc_id} → unwrap 成
 * `AggregateResume`。export.ts 逐份呼叫這支函式，不重複一份 personalGet + unwrap。
 * enc_id 走 encodeURIComponent（同 talents/view.ts 慣例），避免內含特殊字元破壞路徑。
 */
export async function fetchResumeAggregate(opts: PersonalRequestOptions, encId: string): Promise<AggregateResume> {
	const trimmed = encId.trim();
	if (!trimmed) {
		throw new CliError('enc_id must not be empty', ExitCode.InvalidArgument);
	}
	const { body } = await personalGet(opts, `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(trimmed)}`);
	return unwrapDataResponse<AggregateResume>(body);
}

interface SectionSummaryRow {
	section: string;
	summary: string;
}

/**
 * table 模式不印整份巢狀 JSON（太長、不利終端機閱讀，聚合完整內容一律走 `--output json`
 * 或 `--fields`，同 plan Task 13 規格「json 為主用途」）。改印每節摘要：
 * - array 節（education/certificate/language/portfolio_links）→ 筆數
 * - wrapper 節（work_experience）→ `has_no_work_experience` 為 true 時印固定文字，否則印筆數
 * - object / scalar 節（professional_skills/job_condition/background/autobiography）→
 *   present/absent（object 節額外檢查 `Object.keys().length`，避免 `{}` 這種「技術上非 null
 *   但語意上没資料」的殼被誤判成 present）
 * 節的走訪順序沿用 SECTIONS（resume-schema/definition.ts 這份 SoT），避免這裡另建一份節清單、
 * 未來新增/調整節時兩處要同步改。
 */
function summarizeSections(resume: AggregateResume): SectionSummaryRow[] {
	const rows: SectionSummaryRow[] = [{ section: 'name', summary: resume.name }];
	for (const def of SECTIONS) {
		rows.push({ section: def.key, summary: summarizeSection(def, resume) });
	}
	return rows;
}

function summarizeSection(def: SectionDef, resume: AggregateResume): string {
	const value = (resume as unknown as Record<string, unknown>)[def.key];
	if (def.kind === 'array') {
		return `${Array.isArray(value) ? value.length : 0} item(s)`;
	}
	if (def.kind === 'wrapper') {
		const wrapper = value as { has_no_work_experience?: boolean; items?: unknown[] } | null | undefined;
		if (wrapper?.has_no_work_experience) return 'no work experience';
		return `${Array.isArray(wrapper?.items) ? (wrapper.items as unknown[]).length : 0} item(s)`;
	}
	if (def.kind === 'scalar') {
		return typeof value === 'string' && value.length > 0 ? 'present' : 'absent';
	}
	// def.kind === 'object'
	return value && typeof value === 'object' && Object.keys(value as object).length > 0 ? 'present' : 'absent';
}

/**
 * `resumes view` 核心：GET /resumes/{enc_id} → 一次回全節的 AggregateResume（R2）。
 * `--fields` 優先於 `--output`（同 company/jobs/campaigns view 慣例：投影一律印 JSON，
 * 不管 --output table/json），因為投影結果本就是任意巢狀路徑挑出的欄位，表格無從呈現。
 */
export async function runResumesView(ctx: ResolvedContext, encId: string, flags: ViewFlags): Promise<void> {
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const resume = await fetchResumeAggregate(opts, encId);

	if (flags.fields) {
		printJson(pickPaths(resume, parseFieldsList(flags.fields)));
		return;
	}
	if (ctx.format === 'json') {
		printJson(resume);
		return;
	}

	printTable(
		summarizeSections(resume),
		[
			{ header: 'SECTION', value: (r) => r.section, maxWidth: 20 },
			{ header: 'SUMMARY', value: (r) => r.summary, maxWidth: 40 },
		],
		ctx.color
	);
}

export function registerPersonalResumesView(parent: Command): void {
	parent
		.command('view <enc_id>')
		.description('View one resume aggregate (all sections)')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.action(async (encId: string, flags: ViewFlags, command: Command) => {
			await runResumesView(resolveContext(command), encId, flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { summarizeSections };
